import React, { useState } from 'react';
import {
  getICT,
  saveOrderBlock, saveFVG, saveLiquidityLevel, saveMarketStructure,
  updateICTStatus, deleteICT,
} from '../../services/storage';
import { THEME } from '../../styles/theme';

// ── Constants ─────────────────────────────────────────────────────────
const TIMEFRAMES = ['M15', 'H1', 'H4', 'D1', 'W1'];

const STATUS_COLORS = {
  Active:    { color: THEME.colors.green,   bg: 'rgba(0,255,136,0.10)' },
  Mitigated: { color: THEME.colors.textMuted, bg: 'rgba(255,255,255,0.05)' },
  Open:      { color: THEME.colors.blue,    bg: 'rgba(0,212,255,0.10)' },
  Filled:    { color: THEME.colors.textMuted, bg: 'rgba(255,255,255,0.05)' },
  Intact:    { color: THEME.colors.green,   bg: 'rgba(0,255,136,0.10)' },
  Swept:     { color: THEME.colors.red,     bg: 'rgba(255,51,51,0.10)' },
  Bullish:   { color: THEME.colors.green,   bg: 'rgba(0,255,136,0.08)' },
  Bearish:   { color: THEME.colors.red,     bg: 'rgba(255,51,51,0.08)' },
  BOS:       { color: THEME.colors.blue,    bg: 'rgba(0,212,255,0.10)' },
  CHoCH:     { color: THEME.colors.gold,    bg: 'rgba(255,215,0,0.10)' },
};

// ── Shared styles ─────────────────────────────────────────────────────
const S = {
  page:  { padding: '20px', maxWidth: '100%', paddingBottom: '40px' },
  title: { fontSize: '22px', fontWeight: '700', color: THEME.colors.blue, letterSpacing: '2px', fontFamily: THEME.fonts.hud, marginBottom: '4px' },
  sub:   { fontSize: '13px', color: THEME.colors.textMuted, marginBottom: '20px' },
  tabs:  { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: (a) => ({
    padding: '7px 16px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    border:  a ? `1px solid ${THEME.colors.blue}` : '1px solid rgba(255,255,255,0.1)',
    background: a ? 'rgba(0,212,255,0.1)' : 'transparent',
    color:   a ? THEME.colors.blue : THEME.colors.textMuted, transition: 'all 0.15s',
  }),
  card: (borderColor) => ({
    background: 'rgba(255,255,255,0.03)',
    border: `1px solid ${borderColor || 'rgba(255,255,255,0.07)'}`,
    borderRadius: '12px', padding: '14px', marginBottom: '10px',
  }),
  badge: (color, bg) => ({
    padding: '2px 10px', borderRadius: '20px', fontSize: '11px',
    fontWeight: '700', color, background: bg,
    display: 'inline-block', fontFamily: THEME.fonts.hud,
  }),
  input: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: THEME.colors.text, fontSize: '14px',
    padding: '8px 10px', outline: 'none', fontFamily: THEME.fonts.body, boxSizing: 'border-box',
  },
  btn: (v) => ({
    padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: '700',
    background: v === 'primary' ? THEME.colors.blue
              : v === 'danger'  ? 'rgba(255,51,51,0.15)'
              : 'rgba(255,255,255,0.07)',
    color: v === 'primary' ? '#000' : v === 'danger' ? THEME.colors.red : THEME.colors.textMuted,
  }),
  formRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' },
  sectionTitle: {
    fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.textMuted,
    fontFamily: THEME.fonts.hud, marginBottom: '10px', textTransform: 'uppercase',
  },
  priceZone: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.15)',
    borderRadius: '8px', padding: '6px 10px', fontSize: '13px',
    fontFamily: THEME.fonts.hud, color: THEME.colors.gold, fontWeight: '700',
  },
  emptyState: { textAlign: 'center', padding: '40px', color: THEME.colors.textMuted, fontSize: '14px' },
};

function Sel({ style, children, ...props }) {
  return <select style={{ ...S.input, ...style }} {...props}>{children}</select>;
}
function Inp({ style, ...props }) {
  return <input style={{ ...S.input, ...style }} {...props} />;
}

// ── Order Blocks Tab ──────────────────────────────────────────────────
function OrderBlocksTab({ data, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'Bullish', timeframe: 'H1', top: '', bottom: '', notes: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const active = data.order_blocks.filter(ob => ob.status === 'Active');
  const others = data.order_blocks.filter(ob => ob.status !== 'Active');

  const handleSave = () => {
    if (!form.top || !form.bottom) return;
    saveOrderBlock(form);
    setForm({ type: 'Bullish', timeframe: 'H1', top: '', bottom: '', notes: '' });
    setShowForm(false);
    onRefresh();
  };

  return (
    <>
      {!showForm
        ? <button style={{ ...S.btn('primary'), marginBottom: '14px' }} onClick={() => setShowForm(true)}>+ Add Order Block</button>
        : (
          <div style={{ ...S.card('rgba(0,212,255,0.2)'), marginBottom: '14px' }}>
            <div style={S.sectionTitle}>NEW ORDER BLOCK</div>
            <div style={S.formRow}>
              <Sel style={{ flex: 1 }} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="Bullish">Bullish OB</option>
                <option value="Bearish">Bearish OB</option>
              </Sel>
              <Sel style={{ flex: 1 }} value={form.timeframe} onChange={e => set('timeframe', e.target.value)}>
                {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
              </Sel>
            </div>
            <div style={S.formRow}>
              <Inp style={{ flex: 1 }} type="number" placeholder="Top price (e.g. 2648.50)" step="0.01" value={form.top} onChange={e => set('top', e.target.value)} />
              <Inp style={{ flex: 1 }} type="number" placeholder="Bottom price (e.g. 2644.00)" step="0.01" value={form.bottom} onChange={e => set('bottom', e.target.value)} />
            </div>
            <Inp style={{ width: '100%', marginBottom: '10px' }} placeholder="Notes (optional)" value={form.notes} onChange={e => set('notes', e.target.value)} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={S.btn('primary')} onClick={handleSave}>Save OB</button>
              <button style={S.btn()} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )
      }

      {active.length === 0 && others.length === 0 && (
        <div style={S.emptyState}>No Order Blocks logged. Add OBs from your chart analysis.</div>
      )}

      {active.length > 0 && (
        <>
          <div style={S.sectionTitle}>ACTIVE ORDER BLOCKS ({active.length})</div>
          {active.map(ob => <OBCard key={ob.id} ob={ob} onRefresh={onRefresh} />)}
        </>
      )}
      {others.length > 0 && (
        <>
          <div style={{ ...S.sectionTitle, marginTop: '16px' }}>MITIGATED ({others.length})</div>
          {others.map(ob => <OBCard key={ob.id} ob={ob} onRefresh={onRefresh} />)}
        </>
      )}
    </>
  );
}

function OBCard({ ob, onRefresh }) {
  const typeStyle  = STATUS_COLORS[ob.type]  || {};
  const statStyle  = STATUS_COLORS[ob.status] || {};
  const midpoint   = ob.top && ob.bottom ? ((ob.top + ob.bottom) / 2).toFixed(2) : null;

  return (
    <div style={S.card(`${ob.type === 'Bullish' ? 'rgba(0,255,136' : 'rgba(255,51,51'},.2)`)}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={S.badge(typeStyle.color, typeStyle.bg)}>{ob.type}</span>
          <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)')}>{ob.timeframe}</span>
          <span style={S.badge(statStyle.color, statStyle.bg)}>{ob.status}</span>
          <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{ob.id}</span>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {ob.status === 'Active' && (
            <button style={S.btn('danger')} onClick={() => { updateICTStatus('order_blocks', ob.id, 'Mitigated'); onRefresh(); }}>
              Mitigated
            </button>
          )}
          <button style={S.btn('danger')} onClick={() => { deleteICT('order_blocks', ob.id); onRefresh(); }}>✕</button>
        </div>
      </div>
      <div style={S.priceZone}>
        <span>{ob.bottom}</span>
        <span style={{ color: THEME.colors.textMuted }}>—</span>
        <span>{ob.top}</span>
        {midpoint && <span style={{ color: THEME.colors.textMuted, fontSize: '11px' }}>mid {midpoint}</span>}
      </div>
      {ob.notes && <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginTop: '8px' }}>{ob.notes}</div>}
      <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '6px' }}>Formed: {ob.date_formed} · Tested: {ob.tested_count}×</div>
    </div>
  );
}

// ── FVG Tab ───────────────────────────────────────────────────────────
function FVGTab({ data, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'Bullish', timeframe: 'H1', top: '', bottom: '', notes: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const open   = data.fvg.filter(f => f.status === 'Open');
  const filled = data.fvg.filter(f => f.status === 'Filled');

  const handleSave = () => {
    if (!form.top || !form.bottom) return;
    saveFVG(form);
    setForm({ type: 'Bullish', timeframe: 'H1', top: '', bottom: '', notes: '' });
    setShowForm(false);
    onRefresh();
  };

  return (
    <>
      {!showForm
        ? <button style={{ ...S.btn('primary'), marginBottom: '14px' }} onClick={() => setShowForm(true)}>+ Add FVG</button>
        : (
          <div style={{ ...S.card('rgba(255,215,0,0.2)'), marginBottom: '14px' }}>
            <div style={S.sectionTitle}>NEW FAIR VALUE GAP</div>
            <div style={S.formRow}>
              <Sel style={{ flex: 1 }} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="Bullish">Bullish FVG</option>
                <option value="Bearish">Bearish FVG</option>
              </Sel>
              <Sel style={{ flex: 1 }} value={form.timeframe} onChange={e => set('timeframe', e.target.value)}>
                {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
              </Sel>
            </div>
            <div style={S.formRow}>
              <Inp style={{ flex: 1 }} type="number" placeholder="Top of gap" step="0.01" value={form.top} onChange={e => set('top', e.target.value)} />
              <Inp style={{ flex: 1 }} type="number" placeholder="Bottom of gap" step="0.01" value={form.bottom} onChange={e => set('bottom', e.target.value)} />
            </div>
            <Inp style={{ width: '100%', marginBottom: '10px' }} placeholder="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={S.btn('primary')} onClick={handleSave}>Save FVG</button>
              <button style={S.btn()} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )
      }

      {data.fvg.length === 0 && <div style={S.emptyState}>No FVGs logged yet.</div>}

      {open.length > 0 && (
        <>
          <div style={S.sectionTitle}>OPEN GAPS ({open.length})</div>
          {open.map(fvg => (
            <div key={fvg.id} style={S.card(`${fvg.type === 'Bullish' ? 'rgba(0,255,136' : 'rgba(255,51,51'},.15)`)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={S.badge((STATUS_COLORS[fvg.type] || {}).color, (STATUS_COLORS[fvg.type] || {}).bg)}>{fvg.type}</span>
                  <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)')}>{fvg.timeframe}</span>
                  <span style={S.badge(THEME.colors.blue, 'rgba(0,212,255,0.1)')}>OPEN</span>
                  <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{fvg.id}</span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button style={S.btn()} onClick={() => { updateICTStatus('fvg', fvg.id, 'Filled'); onRefresh(); }}>Filled</button>
                  <button style={S.btn('danger')} onClick={() => { deleteICT('fvg', fvg.id); onRefresh(); }}>✕</button>
                </div>
              </div>
              <div style={S.priceZone}><span>{fvg.bottom}</span><span style={{ color: THEME.colors.textMuted }}>—</span><span>{fvg.top}</span><span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>gap: {(fvg.top - fvg.bottom).toFixed(2)}</span></div>
              {fvg.notes && <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginTop: '6px' }}>{fvg.notes}</div>}
            </div>
          ))}
        </>
      )}
      {filled.length > 0 && (
        <>
          <div style={{ ...S.sectionTitle, marginTop: '16px' }}>FILLED ({filled.length})</div>
          {filled.slice(-3).map(fvg => (
            <div key={fvg.id} style={{ ...S.card(), opacity: 0.5 }}>
              <span style={{ fontSize: '13px', color: THEME.colors.textMuted }}>{fvg.id} · {fvg.type} FVG {fvg.timeframe} · {fvg.bottom}–{fvg.top} · Filled</span>
            </div>
          ))}
        </>
      )}
    </>
  );
}

// ── Liquidity Tab ─────────────────────────────────────────────────────
function LiquidityTab({ data, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'BSL', timeframe: 'H4', price: '', notes: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const intact = data.liquidity_levels.filter(l => l.status === 'Intact');
  const swept  = data.liquidity_levels.filter(l => l.status === 'Swept');

  const handleSave = () => {
    if (!form.price) return;
    saveLiquidityLevel(form);
    setForm({ type: 'BSL', timeframe: 'H4', price: '', notes: '' });
    setShowForm(false);
    onRefresh();
  };

  return (
    <>
      {!showForm
        ? <button style={{ ...S.btn('primary'), marginBottom: '14px' }} onClick={() => setShowForm(true)}>+ Add Liquidity Level</button>
        : (
          <div style={{ ...S.card('rgba(0,255,136,0.2)'), marginBottom: '14px' }}>
            <div style={S.sectionTitle}>NEW LIQUIDITY LEVEL</div>
            <div style={S.formRow}>
              <Sel style={{ flex: 1 }} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="BSL">BSL (Buy Side — above highs)</option>
                <option value="SSL">SSL (Sell Side — below lows)</option>
                <option value="EQH">Equal Highs</option>
                <option value="EQL">Equal Lows</option>
              </Sel>
              <Sel style={{ flex: 1 }} value={form.timeframe} onChange={e => set('timeframe', e.target.value)}>
                {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
              </Sel>
            </div>
            <div style={S.formRow}>
              <Inp style={{ flex: 1 }} type="number" placeholder="Price level (e.g. 2658.00)" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} />
              <Inp style={{ flex: 2 }} placeholder="Notes (e.g. Equal highs from Nov 12-14)" value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={S.btn('primary')} onClick={handleSave}>Save Level</button>
              <button style={S.btn()} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )
      }

      {data.liquidity_levels.length === 0 && <div style={S.emptyState}>No liquidity levels tracked. Add BSL/SSL levels from your chart.</div>}

      {intact.length > 0 && (
        <>
          <div style={S.sectionTitle}>INTACT LEVELS ({intact.length})</div>
          {intact.map(liq => {
            const isBSL = ['BSL', 'EQH'].includes(liq.type);
            return (
              <div key={liq.id} style={S.card(isBSL ? 'rgba(0,212,255,0.2)' : 'rgba(255,51,51,0.2)')}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={S.badge(isBSL ? THEME.colors.blue : THEME.colors.red, isBSL ? 'rgba(0,212,255,0.1)' : 'rgba(255,51,51,0.1)')}>{liq.type}</span>
                    <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)')}>{liq.timeframe}</span>
                    <span style={{ fontSize: '16px', fontWeight: '800', color: THEME.colors.gold, fontFamily: THEME.fonts.hud }}>{liq.price}</span>
                    {liq.notes && <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>{liq.notes}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button style={S.btn('danger')} onClick={() => { updateICTStatus('liquidity_levels', liq.id, 'Swept'); onRefresh(); }}>Swept</button>
                    <button style={S.btn('danger')} onClick={() => { deleteICT('liquidity_levels', liq.id); onRefresh(); }}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
      {swept.length > 0 && (
        <>
          <div style={{ ...S.sectionTitle, marginTop: '16px' }}>SWEPT ({swept.length})</div>
          {swept.slice(-5).map(liq => (
            <div key={liq.id} style={{ ...S.card(), opacity: 0.4 }}>
              <span style={{ fontSize: '13px', color: THEME.colors.textMuted }}>{liq.id} · {liq.type} {liq.timeframe} · {liq.price} · Swept</span>
            </div>
          ))}
        </>
      )}
    </>
  );
}

// ── Market Structure Tab ──────────────────────────────────────────────
function MarketStructureTab({ data, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ event: 'BOS', direction: 'Bullish', timeframe: 'H1', price: '', notes: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Current bias per timeframe — last event on each TF
  const tfBias = {};
  [...data.market_structure].reverse().forEach(ms => {
    if (!tfBias[ms.timeframe]) tfBias[ms.timeframe] = ms;
  });

  const handleSave = () => {
    if (!form.price) return;
    saveMarketStructure(form);
    setForm({ event: 'BOS', direction: 'Bullish', timeframe: 'H1', price: '', notes: '' });
    setShowForm(false);
    onRefresh();
  };

  return (
    <>
      {!showForm
        ? <button style={{ ...S.btn('primary'), marginBottom: '14px' }} onClick={() => setShowForm(true)}>+ Log Structure Event</button>
        : (
          <div style={{ ...S.card('rgba(255,215,0,0.2)'), marginBottom: '14px' }}>
            <div style={S.sectionTitle}>NEW STRUCTURE EVENT</div>
            <div style={S.formRow}>
              <Sel style={{ flex: 1 }} value={form.event} onChange={e => set('event', e.target.value)}>
                <option value="BOS">BOS — Break of Structure</option>
                <option value="CHoCH">CHoCH — Change of Character</option>
                <option value="HL">Higher Low formed</option>
                <option value="LH">Lower High formed</option>
              </Sel>
              <Sel style={{ flex: 1 }} value={form.direction} onChange={e => set('direction', e.target.value)}>
                <option value="Bullish">Bullish</option>
                <option value="Bearish">Bearish</option>
              </Sel>
              <Sel style={{ flex: '0 0 90px' }} value={form.timeframe} onChange={e => set('timeframe', e.target.value)}>
                {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
              </Sel>
            </div>
            <div style={S.formRow}>
              <Inp style={{ flex: 1 }} type="number" placeholder="Price where event occurred" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} />
              <Inp style={{ flex: 2 }} placeholder="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={S.btn('primary')} onClick={handleSave}>Log Event</button>
              <button style={S.btn()} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )
      }

      {/* Current bias summary */}
      {Object.keys(tfBias).length > 0 && (
        <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
          <div style={S.sectionTitle}>CURRENT MULTI-TF BIAS</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {['D1','H4','H1','M15'].map(tf => {
              const ms = tfBias[tf];
              if (!ms) return <span key={tf} style={{ ...S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.04)'), padding: '6px 12px' }}>{tf}: —</span>;
              const color = ms.direction === 'Bullish' ? THEME.colors.green : THEME.colors.red;
              return (
                <span key={tf} style={{ ...S.badge(color, `${color}15`), padding: '6px 12px', fontSize: '12px' }}>
                  {tf}: {ms.direction === 'Bullish' ? '▲' : '▼'} {ms.direction} ({ms.event})
                </span>
              );
            })}
          </div>
        </div>
      )}

      {data.market_structure.length === 0 && <div style={S.emptyState}>No structure events logged. Track BOS and CHoCH to build your market map.</div>}

      <div style={S.sectionTitle}>EVENT LOG</div>
      {[...data.market_structure].reverse().slice(0, 20).map(ms => {
        const evStyle = STATUS_COLORS[ms.event] || {};
        const dirStyle = STATUS_COLORS[ms.direction] || {};
        return (
          <div key={ms.id} style={{ ...S.card(), padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={S.badge(evStyle.color || THEME.colors.blue, evStyle.bg || 'rgba(0,212,255,0.1)')}>{ms.event}</span>
            <span style={S.badge(dirStyle.color || THEME.colors.green, dirStyle.bg || 'rgba(0,255,136,0.08)')}>{ms.direction}</span>
            <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)')}>{ms.timeframe}</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: THEME.colors.gold, fontFamily: THEME.fonts.hud }}>{ms.price}</span>
            <span style={{ fontSize: '11px', color: THEME.colors.textMuted, flex: 1 }}>{ms.notes}</span>
            <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{ms.date}</span>
            <button style={{ ...S.btn('danger'), padding: '4px 8px' }} onClick={() => { deleteICT('market_structure', ms.id); onRefresh(); }}>✕</button>
          </div>
        );
      })}
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────────
export default function ICTModule() {
  const [tab, setTab]   = useState('ob');
  const [ict, setICT]   = useState(() => getICT());
  const refresh = () => setICT(getICT());

  const activeOBs  = ict.order_blocks.filter(o => o.status === 'Active').length;
  const openFVGs   = ict.fvg.filter(f => f.status === 'Open').length;
  const intactLiqs = ict.liquidity_levels.filter(l => l.status === 'Intact').length;

  const tabs = [
    { id: 'ob',  label: `Order Blocks (${activeOBs} active)` },
    { id: 'fvg', label: `FVG (${openFVGs} open)` },
    { id: 'liq', label: `Liquidity (${intactLiqs} intact)` },
    { id: 'ms',  label: `Structure (${ict.market_structure.length})` },
  ];

  return (
    <div style={S.page}>
      <div style={S.title}>📐 ICT / SMC MODULE</div>
      <div style={S.sub}>Track Order Blocks, FVGs, Liquidity levels and Market Structure events</div>

      <div style={S.tabs}>
        {tabs.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'ob'  && <OrderBlocksTab     data={ict} onRefresh={refresh} />}
      {tab === 'fvg' && <FVGTab             data={ict} onRefresh={refresh} />}
      {tab === 'liq' && <LiquidityTab       data={ict} onRefresh={refresh} />}
      {tab === 'ms'  && <MarketStructureTab data={ict} onRefresh={refresh} />}
    </div>
  );
}

